import React, { KeyboardEvent, useEffect, useRef, useState } from 'react';
import { ErrorContainer } from '@/components/Approval/LeftContainer/PeopleFilter/styles';

import Input from '@/components/Approval/LeftContainer/Input';
import { FormProvider, useForm } from 'react-hook-form';

import PassportCheckBox from '@/components/Approval/LeftContainer/PeopleFilter/PassportCheckBox';
import { useDebounce } from 'usehooks-ts';
import { useApprovalPassportSearch } from '@/hooks/apiHooks';
import { useAtom } from 'jotai';
import { approvalFilter, approvalTempFilter } from '@/recoil/atom';
import SearchPassports from '@/components/Approval/LeftContainer/PeopleFilter/SearchPassports';
import {
  AllFilterName,
  FilterPassport,
  Passport,
  PeopleFilterName,
} from '@/types/api';
import { useTranslation } from 'react-i18next';
import AllFilterOption from '@/components/Approval/LeftContainer/PeopleFilter/AllFilterOption';
import { GTMEventName, WorkEventName, WorkPageAreaType } from '@/constant/gtm';
import { useWorkEventTracker } from '@/hooks/gtm/useGTMEventTracker';

type PeopleFilterPanelProps = {
  pageArea: WorkPageAreaType;
  title: string;
  filterName: PeopleFilterName;
  recommend?: Passport[];
  allFilterName?: AllFilterName;
};

/**
 * PeopleFilter 의 편집 본문(검색 + 선택/추천 리스트).
 * 아코디언 헤더/open 상태 없이 항상 렌더되며, FilterChipPopover 안에서 호스팅된다.
 * 상태는 전역 approvalFilter/approvalTempFilter atom 을 그대로 사용.
 */
const PeopleFilterPanel = (props: PeopleFilterPanelProps) => {
  const { pageArea, title, filterName, recommend, allFilterName } = props;
  const { t } = useTranslation();
  const workEventTracker = useWorkEventTracker(pageArea);
  const [error, setError] = useState(false);
  const [tempFilters, setTempFilters] = useAtom(approvalTempFilter);
  const [filters, setFilters] = useAtom(approvalFilter);

  // 팝오버를 (다시) 열 때 선택 목록을 현재 선택값 기준으로 리셋 — 해제 후 재오픈 시 잔여 항목 방지
  useEffect(() => {
    setTempFilters((prev) => ({ ...prev, [filterName]: filters[filterName] }));
    // eslint-disable-next-line
  }, []);

  const recommendItems =
    recommend?.filter(
      (item1) =>
        !(tempFilters[filterName] || []).some(
          (item2) => item1.passportId === item2.passportId,
        ),
    ) ?? [];
  const methods = useForm({
    defaultValues: { keyword: '', [filterName]: [] },
  });

  const searchString = methods.watch('keyword');
  const debouncedValue = useDebounce(searchString, 500);

  // 전자결재 전용 노출 설정 기준 검색 — 파견직도 조회 조건으로 지정할 수 있어야 한다 (PORTAL-14029)
  // activated 빈 값 = 재직 필터를 걸지 않음. 퇴사자가 작성·결재한 문서도 찾을 수 있어야 한다.
  // 티켓 문구는 "activated 를 전달하지 않음" 이지만 생략하면 서버가 재직자만 반환한다(dev 실측) — 빼지 말 것
  const { data } = useApprovalPassportSearch(
    { keyword: debouncedValue, listSize: 50, activated: '' },
    2,
  );

  const searchingData = data?.data;

  const handleError = () => {
    setError(true);
    setTimeout(() => {
      setError(false);
    }, 5000);
  };
  const scrollLayout = useRef<HTMLDivElement>(null);
  const [isMovingMouse, setIsMovingMouse] = useState(false);

  const [cursor, setCursor] = useState(-1);
  // 키보드 탐색이 시작됐는지(첫 ArrowDown 은 무조건 맨 위부터). 검색/마우스 변경 시 리셋
  const navStartedRef = useRef(false);
  // 같은 물리 키 이벤트가 두 번 들어오는 경우 방지
  const lastKeyTsRef = useRef(0);

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (!searchingData) return;
    if (event.timeStamp === lastKeyTsRef.current) return; // 중복 이벤트 무시
    lastKeyTsRef.current = event.timeStamp;

    switch (event.key) {
      case 'ArrowDown': {
        event.preventDefault();
        setIsMovingMouse(false);
        if (!navStartedRef.current) {
          navStartedRef.current = true;
          setCursor(0);
        } else {
          setCursor((prev) => Math.min(prev + 1, searchingData.length - 1));
        }
        break;
      }
      case 'ArrowUp':
        event.preventDefault();
        setIsMovingMouse(false);
        navStartedRef.current = true;
        setCursor((prev) => Math.max(prev - 1, 0));
        break;
      case 'Enter': {
        if (searchingData[cursor]) {
          handleCustomClick({
            passportId: searchingData[cursor].passportId,
            profileImageUrl: searchingData[cursor].detail.profileImageUrl,
            positionName: searchingData[cursor].detail.positionName,
            name: searchingData[cursor].detail.name,
            divisionName: searchingData[cursor].detail.divisionName,
            departmentName: searchingData[cursor].detail.departmentName,
          });
        }
        break;
      }
      case 'Escape':
        setCursor(-1);
        methods.resetField('keyword');
        break;
    }
  };

  const handleMouseMove = (index: number) => {
    setIsMovingMouse(true);
    // 마우스로 hover 하면 다음 키보드 입력은 다시 맨 위부터 시작
    navStartedRef.current = false;
    setCursor(index);
  };

  useEffect(() => {
    if (!isMovingMouse && cursor >= 0) {
      // SearchPassports 첫 자식은 '검색결과 N건' 헤더라 실제 항목은 cursor + 1 위치
      const targetElement = scrollLayout.current?.children[
        cursor + 1
      ] as HTMLElement;
      if (targetElement) {
        targetElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }
  }, [isMovingMouse, cursor]);

  useEffect(() => {
    setCursor(-1);
    navStartedRef.current = false;
    if (debouncedValue.length > 0) {
      // GA Work - Approval 9번
      workEventTracker(GTMEventName.searchKeyword, {
        area_name: title,
        keyword: debouncedValue,
      });
    }
  }, [debouncedValue]);

  const handleCustomClick = (passport: FilterPassport) => {
    const {
      passportId,
      profileImageUrl,
      positionName,
      name,
      divisionName,
      departmentName,
    } = passport;
    const isChecked = filters[filterName].some(
      (res: FilterPassport) => res.passportId === passportId,
    );
    if (isChecked) {
      setFilters({
        ...filters,
        [filterName]: filters[filterName].filter(
          (res: FilterPassport) => res.passportId !== passportId,
        ),
      });
    } else {
      if (filters[filterName].length >= 10) {
        handleError();
      } else {
        setFilters({
          ...filters,
          [filterName]: [
            ...filters[filterName],
            {
              passportId,
              profileImageUrl,
              positionName,
              name,
              divisionName,
              departmentName,
            },
          ],
        });
      }
    }

    onChange(!isChecked, passport, '검색어 추천');

    if (
      !(tempFilters[filterName] || []).some(
        (item) => item.passportId === passportId,
      )
    ) {
      setTempFilters({
        ...tempFilters,
        [filterName]: [
          ...(tempFilters[filterName] || []),
          {
            passportId,
            profileImageUrl,
            positionName,
            name,
            divisionName,
            departmentName,
          },
        ],
      });
    }
  };

  const onChange = (
    isChecked: boolean,
    passport: FilterPassport,
    description = '품의 필터',
  ) => {
    // GA Work - Approval 10번
    workEventTracker(WorkEventName.clickEvent, {
      area_name: title,
      contents: `${passport.name} ${passport.positionName}`,
      status: isChecked,
      description,
    });
  };

  return (
    <FormProvider {...methods}>
      {allFilterName && (
        <AllFilterOption
          checked={!!filters[allFilterName]}
          onChange={(all) => setFilters({ ...filters, [allFilterName]: all })}
        />
      )}
      <Input
        value={'keyword'}
        handleKeyDown={handleKeyDown}
        onBlur={() => setCursor(-1)}
        bleed
      />
      {/* 검색결과는 입력창 포커스와 무관하게 표시 (필터옵션 팝오버 열어도 유지) */}
      {debouncedValue && (
        <SearchPassports
          ref={scrollLayout}
          currCursorIndex={cursor}
          filterName={filterName}
          data={searchingData}
          keyword={debouncedValue}
          handleError={handleError}
          onMouseMove={handleMouseMove}
          onChange={(...params) => onChange(...params, '검색어 추천')}
        />
      )}
      {error && (
        <ErrorContainer>{t('최대 10명까지 선택할 수 있어요.')}</ErrorContainer>
      )}
      <div
        style={{
          display: debouncedValue ? 'none' : 'flex',
          flexDirection: 'column',
          gap: '8px',
          padding: '8px',
        }}
      >
        {(tempFilters[filterName] || []).map((res) => (
          <PassportCheckBox
            key={`${filterName}-${res.passportId}`}
            passportId={res.passportId}
            profileImageUrl={res.profileImageUrl}
            positionName={res.positionName}
            name={res.name}
            divisionName={res.divisionName}
            departmentName={res.departmentName}
            filterName={filterName}
            handleError={handleError}
            onChange={onChange}
          />
        ))}
        {recommendItems.length > 0 && (
          <>
            {(tempFilters[filterName] || []).length > 0 && (
              <div
                style={{
                  height: '1px',
                  background: 'var(--dk-bg-strong, #e7e9ec)',
                  margin: '0 -8px',
                }}
              />
            )}
            <div
              style={{
                color: 'var(--dk-text-secondary, #888D96)',
                fontSize: '12px',
                fontWeight: 500,
                lineHeight: '18px',
              }}
            >
              {t('추천')}
            </div>
            {recommendItems.map((res) => (
              <PassportCheckBox
                key={`${filterName}-${res.passportId}`}
                passportId={res.passportId}
                profileImageUrl={res.detail.profileImageUrl}
                positionName={res.detail.positionName}
                name={res.detail.name}
                divisionName={res.detail.divisionName}
                departmentName={res.detail.departmentName}
                filterName={filterName}
                handleError={handleError}
                onChange={onChange}
              />
            ))}
          </>
        )}
      </div>
    </FormProvider>
  );
};

export default PeopleFilterPanel;
